import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { ApiRequestError } from '../../api/client.ts';
import { useRegister } from '../../api/hooks/useAuth.ts';
import {
  AuthCard,
  buttonClass,
  Field,
  FormError,
  FormNotice,
  inputClass,
} from '../../components/AuthCard.tsx';

export default function Register() {
  const register = useRegister();
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    register.mutate({ email, password, display_name: displayName });
  }

  if (register.isSuccess) {
    return (
      <AuthCard title="Check your inbox">
        <FormNotice
          message={`We sent a verification link to ${register.data.user.email}. Open it to activate your account, then sign in.`}
        />
        <Link className="block text-center text-sm text-blue-600 hover:underline" to="/login">
          Back to sign in
        </Link>
      </AuthCard>
    );
  }

  // "That address is taken" is a dead end unless we say what to do about it — and the
  // commonest cause is the user's own earlier signup whose password they've forgotten.
  const emailTaken =
    register.error instanceof ApiRequestError && register.error.code === 'email_taken';

  return (
    <AuthCard title="Create your account">
      <form onSubmit={onSubmit}>
        <FormError message={emailTaken ? undefined : register.error?.message} />
        {emailTaken && (
          <div className="mb-4 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            <p>That email address already has an account.</p>
            <p className="mt-2">
              <Link className="text-blue-600 hover:underline" to="/login">
                Sign in
              </Link>{' '}
              instead, or{' '}
              <Link className="text-blue-600 hover:underline" to="/forgot-password">
                reset your password
              </Link>{' '}
              if you've forgotten it — that works even if you never confirmed the address.
            </p>
          </div>
        )}
        <Field label="Name">
          <input
            className={inputClass}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            autoComplete="name"
            required
          />
        </Field>
        <Field label="Email">
          <input
            className={inputClass}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
        </Field>
        <Field label="Password (10+ characters)">
          <input
            className={inputClass}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            minLength={10}
            required
          />
        </Field>
        <button className={buttonClass} type="submit" disabled={register.isPending}>
          {register.isPending ? 'Creating…' : 'Create account'}
        </button>
      </form>
      <p className="mt-6 text-center text-sm text-gray-500">
        Already registered?{' '}
        <Link className="text-blue-600 hover:underline" to="/login">
          Sign in
        </Link>
      </p>
    </AuthCard>
  );
}
